import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Checkbox } from 'designSystem/components/Checkbox'
import { useOptionalSidePanelPresentation } from 'designSystem/components/sidePanelContext'
import { Text } from 'designSystem/components/Text'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import {
  formatCitationTimestamp,
  formatDatapointValue,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/datapointValueCitations'
import { hasInProgressDocChatTurn } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import {
  pickedLabelsFromHiddenContext,
  stageTagPick,
  unstageTagPick,
  useStagedTagPicks,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatTagPicks'
import type {
  TimeSeriesDatapointMatch,
  TimeSeriesIdentifierResolution,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { PreviewTrendHandler } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import type { DocChatTurn } from 'features/knowledgeDocumentViewer/types'
import { ArrowRight, ChartLine, ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useCallback, useId, useMemo, useState } from 'react'
import styled from 'styled-components'

import { InlineTurnCard } from './docChatStyles'

const VISIBLE_CANDIDATES = 5

const EMPTY_TURNS: DocChatTurn[] = []

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const StepNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
`

const CandidateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem 0.5rem;
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border2};
  border-radius: 0.375rem;
`

const CandidateLabel = styled.label<{ $locked: boolean }>`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
`

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

function matchLabel(match: TimeSeriesDatapointMatch): string {
  return match.pi_tag ?? match.name ?? match.entry_id
}

function latestReadingText(match: TimeSeriesDatapointMatch): string | null {
  if (match.latest_value === null || match.latest_value === undefined) {
    return null
  }
  const value = formatDatapointValue(match.latest_value, match.units)
  const timestamp = formatCitationTimestamp(match.latest_timestamp)
  return timestamp ? t`${value} as of ${timestamp}` : value
}

/**
 * The entry ids a later turn already sent for this identifier, or null while
 * it is still open. Only turns after the card's own count, so a re-asked
 * ambiguity starts fresh.
 */
function consumedEntryIds(
  laterTurns: DocChatTurn[],
  resolution: TimeSeriesIdentifierResolution
): string[] | null {
  const candidateLabels = resolution.matches.map(matchLabel)
  for (let index = laterTurns.length - 1; index >= 0; index--) {
    const turn = laterTurns[index]
    const selected =
      turn.tagSelections?.filter(
        (selection) => selection.identifier === resolution.identifier
      ) ?? []
    if (selected.length > 0) {
      return selected.map((selection) => selection.entryId)
    }
    const labels = pickedLabelsFromHiddenContext(
      turn.hiddenContext,
      resolution.identifier,
      candidateLabels
    )
    if (labels.length > 0) {
      return resolution.matches
        .filter((match) => labels.includes(matchLabel(match)))
        .map((match) => match.entry_id)
    }
  }
  return null
}

function CandidateOption({
  match,
  picked,
  locked,
  onToggle,
  onPreviewTrend,
}: {
  match: TimeSeriesDatapointMatch
  picked: boolean
  locked: boolean
  onToggle: (match: TimeSeriesDatapointMatch, picked: boolean) => void
  onPreviewTrend?: PreviewTrendHandler
}): React.ReactElement {
  useLingui()
  const checkboxId = useId()
  const sidePanel = useOptionalSidePanelPresentation()
  const label = matchLabel(match)
  const reading = latestReadingText(match)

  const handleOpenTrend = useCallback(() => {
    if (!onPreviewTrend) return
    sidePanel?.keepOpenOnNextOutsideClick()
    onPreviewTrend({ entryIds: [match.entry_id], label })
  }, [label, match.entry_id, onPreviewTrend, sidePanel])

  return (
    <CandidateRow>
      <Checkbox
        id={checkboxId}
        checked={picked}
        disabled={locked}
        onCheckedChange={(checked) => onToggle(match, checked === true)}
      />
      <CandidateLabel htmlFor={checkboxId} $locked={locked}>
        <Text variant='caption' weight='medium' truncate>
          {label}
          {reading ? ` · ${reading}` : ''}
        </Text>
        {match.description ? (
          <Text variant='caption' color='muted' truncate>
            {match.description}
          </Text>
        ) : null}
      </CandidateLabel>
      {onPreviewTrend ? (
        <Button
          type='button'
          variant='ghost'
          size='icon-sm'
          aria-label={t`Open a trend for ${label}`}
          title={t`Open trend`}
          onClick={handleOpenTrend}>
          <ChartLine />
        </Button>
      ) : null}
    </CandidateRow>
  )
}

function CandidateList({
  resolution,
  pickedEntryIds,
  locked,
  onToggle,
  onPreviewTrend,
}: {
  resolution: TimeSeriesIdentifierResolution
  pickedEntryIds: string[]
  locked: boolean
  onToggle: (match: TimeSeriesDatapointMatch, picked: boolean) => void
  onPreviewTrend?: PreviewTrendHandler
}): React.ReactElement {
  const [expanded, setExpanded] = useState(false)

  const hasPickBelowFold = resolution.matches.some(
    (match, index) =>
      index >= VISIBLE_CANDIDATES && pickedEntryIds.includes(match.entry_id)
  )
  const matches =
    expanded || hasPickBelowFold
      ? resolution.matches
      : resolution.matches.slice(0, VISIBLE_CANDIDATES)
  const hiddenCount = resolution.matches.length - matches.length

  return (
    <>
      {matches.map((match) => (
        <CandidateOption
          key={match.entry_id}
          match={match}
          picked={pickedEntryIds.includes(match.entry_id)}
          locked={locked}
          onToggle={onToggle}
          onPreviewTrend={onPreviewTrend}
        />
      ))}
      {hiddenCount > 0 ? (
        <Button
          type='button'
          variant='ghost'
          size='xs'
          onClick={() => setExpanded(true)}>
          <Trans>Show {hiddenCount} more</Trans>
        </Button>
      ) : null}
    </>
  )
}

function TagPickerQuestions({
  questions,
  turnId,
  onPreviewTrend,
}: {
  questions: TimeSeriesIdentifierResolution[]
  turnId: string
  onPreviewTrend?: PreviewTrendHandler
}): React.ReactElement {
  useLingui()
  const { tabId, searchState, siteId, submitStagedTagPicks } =
    useAdvancedSearchUX()
  const stagedPicks = useStagedTagPicks(tabId)

  const docChat = searchState.docChat
  const turns = useMemo(
    () =>
      docChat?.siteId === siteId ? docChat.conversation.turns : EMPTY_TURNS,
    [docChat, siteId]
  )
  const isTurnInProgress = hasInProgressDocChatTurn(turns)
  const consumedByIdentifier = useMemo(() => {
    const ownIndex = turns.findIndex((turn) => turn.id === turnId)
    const laterTurns = turns.slice(ownIndex + 1)
    return new Map(
      questions.map((question) => [
        question.identifier,
        consumedEntryIds(laterTurns, question),
      ])
    )
  }, [questions, turnId, turns])

  const [step, setStep] = useState(() =>
    Math.max(
      questions.findIndex(
        (question) =>
          consumedByIdentifier.get(question.identifier) === null &&
          !stagedPicks.some((pick) => pick.identifier === question.identifier)
      ),
      0
    )
  )
  const stepIndex = Math.min(step, questions.length - 1)
  const question = questions[stepIndex]
  const identifier = question.identifier
  const isLastStep = stepIndex === questions.length - 1

  const consumed = consumedByIdentifier.get(identifier) ?? null
  const locked = consumed !== null
  const pickedEntryIds =
    consumed ??
    stagedPicks
      .filter((pick) => pick.identifier === identifier)
      .map((pick) => pick.entryId)
  const cardPicks = stagedPicks.filter(
    (pick) => consumedByIdentifier.get(pick.identifier) === null
  )
  const stagedCount = cardPicks.length
  const stagedLabel = cardPicks[0]?.label

  const handleToggle = useCallback(
    (match: TimeSeriesDatapointMatch, picked: boolean) => {
      const pick = {
        identifier,
        entryId: match.entry_id,
        label: matchLabel(match),
      }
      if (picked) {
        stageTagPick(tabId, pick)
      } else {
        unstageTagPick(tabId, pick)
      }
    },
    [identifier, tabId]
  )

  const current = stepIndex + 1
  const total = questions.length

  return (
    <InlineTurnCard>
      <QuestionHeader>
        <Text variant='caption' color='muted'>
          <Trans>Which tag did you mean for “{identifier}”?</Trans>
        </Text>
        {total > 1 ? (
          <StepNav>
            <Button
              type='button'
              variant='ghost'
              size='icon-xs'
              aria-label={t`Previous question`}
              disabled={stepIndex === 0}
              onClick={() => setStep(stepIndex - 1)}>
              <ChevronLeft />
            </Button>
            <Text variant='caption' color='muted'>
              <Trans>
                {current} of {total}
              </Trans>
            </Text>
            <Button
              type='button'
              variant='ghost'
              size='icon-xs'
              aria-label={t`Next question`}
              disabled={isLastStep}
              onClick={() => setStep(stepIndex + 1)}>
              <ChevronRight />
            </Button>
          </StepNav>
        ) : null}
      </QuestionHeader>
      <Box display='flex' direction='column' gap='xs' minWidth='none'>
        <CandidateList
          key={identifier}
          resolution={question}
          pickedEntryIds={pickedEntryIds}
          locked={locked}
          onToggle={handleToggle}
          onPreviewTrend={onPreviewTrend}
        />
      </Box>
      <FooterRow>
        <Text variant='caption' color='muted'>
          {stagedCount > 1 ? (
            <Trans>
              {stagedCount} tags selected — they will apply to your next
              message.
            </Trans>
          ) : stagedCount === 1 ? (
            <Trans>
              {stagedLabel} selected — it will apply to your next message.
            </Trans>
          ) : locked ? null : (
            <Trans>Select every tag that applies.</Trans>
          )}
        </Text>
        {isLastStep ? (
          stagedCount > 0 ? (
            <Button
              type='button'
              variant='outline'
              size='xs'
              disabled={isTurnInProgress}
              onClick={submitStagedTagPicks}>
              <Trans>Apply selection</Trans>
              <ArrowRight size={12} />
            </Button>
          ) : null
        ) : (
          <Button
            type='button'
            variant='outline'
            size='xs'
            onClick={() => setStep(stepIndex + 1)}>
            {pickedEntryIds.length > 0 ? (
              <Trans>Next</Trans>
            ) : (
              <Trans>Skip</Trans>
            )}
            <ArrowRight size={12} />
          </Button>
        )}
      </FooterRow>
    </InlineTurnCard>
  )
}

/**
 * Inline disambiguation for a turn whose identifiers matched several tags:
 * one question per identifier, stepped through in turn, each taking any
 * number of tags. Renders from the persisted response event, so it survives
 * reloads.
 */
export function DocChatTagPickerCard({
  resolutions,
  turnId,
  onPreviewTrend,
}: {
  resolutions: TimeSeriesIdentifierResolution[]
  turnId: string
  onPreviewTrend?: PreviewTrendHandler
}): React.ReactElement | null {
  const questions = useMemo(
    () =>
      resolutions.filter(
        (resolution) =>
          resolution.status === 'ambiguous' && resolution.matches.length > 0
      ),
    [resolutions]
  )
  if (questions.length === 0) return null

  return (
    <TagPickerQuestions
      questions={questions}
      turnId={turnId}
      onPreviewTrend={onPreviewTrend}
    />
  )
}
